import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Empty,
  Input,
  Modal,
  Select,
  Table,
  message,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import DashboardPageWrapper from '../../../components/layout/DashboardPageWrapper';
import AppRangePicker from '../../../components/common/AppRangePicker';
import { useAuth } from '../../../contexts/AuthContext';
import { PERMISSIONS } from '../../../config/permissions';
import offlineProductSalesService from '../../../services/offlineProductSalesService';
import orderService from '../../../services/orderService';
import ifaService from '../../../services/ifaService';
import OfflineProductSalesFormDrawer from './OfflineProductSalesFormDrawer';
import { getOfflineProductSalesColumns } from './offlineProductSalesColumns';
import {
  COMMISSION_STATUS_OPTIONS,
  DEFAULT_PAGE_SIZE,
  OFFLINE_IFA_PARTNER_LIST_PARAMS,
} from './offlineProductSales.constants';
import {
  apiErrMsg,
  filterProductTypesForFilter,
} from './offlineProductSales.helpers';

function buildFiltersFromUrl(searchParams) {
  return {
    search: searchParams.get('search') || '',
    product_type: searchParams.get('product_type') || '',
    rm_uuid: searchParams.get('rm_uuid') || '',
    commission_paid_status: searchParams.get('commission_paid_status') || '',
    start_date: searchParams.get('start_date') || '',
    end_date: searchParams.get('end_date') || '',
  };
}

function parseDateRangeFromParams(searchParams) {
  const start = searchParams.get('start_date');
  const end = searchParams.get('end_date');
  return start && end ? [dayjs(start), dayjs(end)] : null;
}

export default function OfflineProductSalesListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage = isSuperAdmin || hasPermission(PERMISSIONS.OFFLINE_PRODUCT_SALES_MANAGE);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productTypes, setProductTypes] = useState([]);
  const [rmList, setRmList] = useState([]);
  const [rmUnavailable, setRmUnavailable] = useState(false);
  const [partnerUnavailable, setPartnerUnavailable] = useState(false);

  const [filters, setFilters] = useState(() => buildFiltersFromUrl(searchParams));
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
  const [dateRange, setDateRange] = useState(() => parseDateRangeFromParams(searchParams));
  const [pagination, setPagination] = useState(() => {
    const page = Number(searchParams.get('page'));
    return {
      current: page > 0 ? page : 1,
      pageSize: DEFAULT_PAGE_SIZE,
      total: 0,
    };
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingUuid, setEditingUuid] = useState(null);

  const filtersRef = useRef(filters);
  const paginationRef = useRef(pagination);
  useEffect(() => { filtersRef.current = filters; }, [filters]);
  useEffect(() => { paginationRef.current = pagination; }, [pagination]);

  const rmOptions = useMemo(
    () =>
      rmList
        .map((rm) => {
          const value = rm?.uuid ?? rm?.id;
          if (!value) return null;
          return { value: String(value), label: rm?.name || String(value) };
        })
        .filter(Boolean),
    [rmList],
  );

  const filterProductTypeOptions = useMemo(
    () => filterProductTypesForFilter(productTypes).map((type) => ({ value: type, label: type })),
    [productTypes],
  );

  const fetchList = useCallback(async (paramsObj = {}) => {
    setLoading(true);
    try {
      const page = paramsObj.page ?? paginationRef.current.current;
      const limit = paramsObj.pageSize ?? paginationRef.current.pageSize ?? DEFAULT_PAGE_SIZE;
      const currentFilters = paramsObj.filters ?? filtersRef.current;

      const apiParams = {
        page,
        limit,
      };
      if (currentFilters.search?.trim()) apiParams.search = currentFilters.search.trim();
      if (currentFilters.product_type) apiParams.product_type = currentFilters.product_type;
      if (currentFilters.rm_uuid) apiParams.rm_uuid = currentFilters.rm_uuid;
      if (currentFilters.commission_paid_status) {
        apiParams.commission_paid_status = currentFilters.commission_paid_status;
      }
      if (currentFilters.start_date) apiParams.start_date = currentFilters.start_date;
      if (currentFilters.end_date) apiParams.end_date = currentFilters.end_date;

      const { items: list, pagination: pag } = await offlineProductSalesService.list(apiParams);
      setItems(list);
      setPagination((prev) => ({
        ...prev,
        current: pag.page ?? page,
        pageSize: pag.limit ?? limit,
        total: pag.total ?? 0,
      }));
    } catch (err) {
      message.error(apiErrMsg(err));
      setItems([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchListRef = useRef(fetchList);
  useEffect(() => { fetchListRef.current = fetchList; }, [fetchList]);

  const syncUrl = useCallback(
    (nextFilters, nextPagination) => {
      const params = new URLSearchParams();
      if (nextFilters.search?.trim()) params.set('search', nextFilters.search.trim());
      if (nextFilters.product_type) params.set('product_type', nextFilters.product_type);
      if (nextFilters.rm_uuid) params.set('rm_uuid', nextFilters.rm_uuid);
      if (nextFilters.commission_paid_status) {
        params.set('commission_paid_status', nextFilters.commission_paid_status);
      }
      if (nextFilters.start_date) params.set('start_date', nextFilters.start_date);
      if (nextFilters.end_date) params.set('end_date', nextFilters.end_date);
      if (nextPagination.current > 1) params.set('page', String(nextPagination.current));
      setSearchParams(params, { replace: true });
    },
    [setSearchParams],
  );

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      offlineProductSalesService.getProductTypes(),
      orderService.listOrderRms().catch((err) => {
        if (err?.response?.status === 403) {
          setRmUnavailable(true);
          return [];
        }
        throw err;
      }),
      ifaService.getAdminIfas({
        ...OFFLINE_IFA_PARTNER_LIST_PARAMS,
        page: 1,
        limit: 1,
      }).catch((err) => {
        if (err?.response?.status === 403) {
          setPartnerUnavailable(true);
          return null;
        }
        throw err;
      }),
    ])
      .then(([types, rms]) => {
        if (cancelled) return;
        setProductTypes(Array.isArray(types) ? types : []);
        setRmList(Array.isArray(rms) ? rms : []);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error(err);
          message.error(apiErrMsg(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    fetchList({ page: pagination.current, pageSize: pagination.pageSize, filters });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput === filtersRef.current.search) return;
      const nextFilters = { ...filtersRef.current, search: searchInput };
      const nextPagination = { ...paginationRef.current, current: 1 };
      setFilters(nextFilters);
      setPagination(nextPagination);
      syncUrl(nextFilters, nextPagination);
      fetchListRef.current({ page: 1, filters: nextFilters });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, syncUrl]);

  const applyFilterChange = (patch) => {
    const nextFilters = { ...filters, ...patch };
    const nextPagination = { ...pagination, current: 1 };
    setFilters(nextFilters);
    setPagination(nextPagination);
    syncUrl(nextFilters, nextPagination);
    fetchListRef.current({ page: 1, filters: nextFilters });
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    applyFilterChange({
      start_date: range?.[0] ? range[0].format('YYYY-MM-DD') : '',
      end_date: range?.[1] ? range[1].format('YYYY-MM-DD') : '',
    });
  };

  const handleClearFilters = () => {
    const cleared = {
      search: '',
      product_type: '',
      rm_uuid: '',
      commission_paid_status: '',
      start_date: '',
      end_date: '',
    };
    setSearchInput('');
    setDateRange(null);
    setFilters(cleared);
    const nextPagination = { ...pagination, current: 1 };
    setPagination(nextPagination);
    syncUrl(cleared, nextPagination);
    fetchListRef.current({ page: 1, filters: cleared });
  };

  const handleTableChange = (pag) => {
    const nextPage = pag.current ?? 1;
    const nextPageSize = pag.pageSize ?? DEFAULT_PAGE_SIZE;
    if (nextPage === pagination.current && nextPageSize === pagination.pageSize) return;

    const nextPagination = { ...pagination, current: nextPage, pageSize: nextPageSize };
    setPagination(nextPagination);
    syncUrl(filters, nextPagination);
    fetchListRef.current({ page: nextPage, pageSize: nextPageSize, filters });
  };

  const openCreate = () => {
    setEditingUuid(null);
    setDrawerOpen(true);
  };

  const openEdit = (record) => {
    setEditingUuid(record.uuid);
    setDrawerOpen(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Delete this offline sale?',
      content: 'This cannot be undone.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await offlineProductSalesService.delete(record.uuid);
          message.success('Offline sale deleted successfully');
          fetchListRef.current({ page: paginationRef.current.current, filters: filtersRef.current });
        } catch (err) {
          message.error(apiErrMsg(err));
        }
      },
    });
  };

  const handleFormSuccess = () => {
    message.success(editingUuid ? 'Offline sale updated successfully' : 'Offline sale created successfully');
    offlineProductSalesService.getProductTypes().then(setProductTypes).catch(() => {});
    fetchListRef.current({ page: paginationRef.current.current, filters: filtersRef.current });
  };

  const goToPartnerProfile = useCallback(
    (record) => {
      const partnerUuid = record?.ifa?.uuid;
      if (!partnerUuid) return;
      navigate(`/partner/details/${encodeURIComponent(String(partnerUuid))}`);
    },
    [navigate],
  );

  const columns = useMemo(
    () =>
      getOfflineProductSalesColumns({
        canManage,
        onEdit: openEdit,
        onDelete: handleDelete,
        onPartnerClick: goToPartnerProfile,
      }),
    [canManage, goToPartnerProfile],
  );

  const hasActiveFilters =
    filters.search ||
    filters.product_type ||
    filters.rm_uuid ||
    filters.commission_paid_status ||
    filters.start_date ||
    filters.end_date;

  return (
    <DashboardPageWrapper>
      <div className="p-4 sm:p-6 flex flex-col flex-1 min-h-0">
        <div className="mb-4 flex flex-wrap justify-between items-start gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Offline Transactions</h1>
            <p className="text-sm text-gray-600 mt-1">
              Track offline product sales and Altcase revenue by RM
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button icon={<ReloadOutlined />} onClick={() => fetchListRef.current()} loading={loading}>
              Refresh
            </Button>
            {canManage && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                Add Offline Transaction
              </Button>
            )}
          </div>
        </div>

        {rmUnavailable && (
          <Alert
            type="warning"
            showIcon
            className="mb-4"
            message="RM list unavailable"
            description="Assign PRODUCT_PRE_IPO_ORDER_MANAGEMENT_VIEW to this role, or contact an admin."
          />
        )}

        {partnerUnavailable && (
          <Alert
            type="warning"
            showIcon
            className="mb-4"
            message="IFA partner list unavailable"
            description="Assign PARTNER_VIEW to this role, or contact an admin."
          />
        )}

        <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          <Input
            placeholder="Search buyer, PAN, RM or IFA name"
            allowClear
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <Select
            placeholder="Product type"
            allowClear
            value={filters.product_type || undefined}
            onChange={(v) => applyFilterChange({ product_type: v ?? '' })}
            options={filterProductTypeOptions}
            showSearch
            optionFilterProp="label"
          />
          <Select
            placeholder="RM"
            allowClear
            value={filters.rm_uuid || undefined}
            onChange={(v) => applyFilterChange({ rm_uuid: v ?? '' })}
            options={rmOptions}
            showSearch
            optionFilterProp="label"
            disabled={rmUnavailable}
          />
          <Select
            placeholder="Commission status"
            allowClear
            value={filters.commission_paid_status || undefined}
            onChange={(v) => applyFilterChange({ commission_paid_status: v ?? '' })}
            options={COMMISSION_STATUS_OPTIONS}
          />
          <AppRangePicker
            className="w-full"
            value={dateRange}
            onChange={handleDateRangeChange}
            format="DD MMM YYYY"
            placeholder={['Start date', 'End date']}
          />
          {hasActiveFilters && (
            <Button onClick={handleClearFilters} className="w-full sm:w-auto">
              Clear filters
            </Button>
          )}
        </div>

        <div className="flex-1 min-h-0 overflow-x-auto">
          <Table
            rowKey="uuid"
            columns={columns}
            dataSource={items}
            loading={loading}
            tableLayout="fixed"
            className="min-w-[1538px] [&_.ant-table-thead>tr>th]:whitespace-nowrap [&_.ant-table-tbody>tr>td]:overflow-hidden"
            scroll={{ x: 1538 }}
            locale={{
              emptyText: (
                <Empty
                  description="No offline sales yet"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                >
                  {canManage && (
                    <Button type="primary" onClick={openCreate}>
                      Add offline sale
                    </Button>
                  )}
                </Empty>
              ),
            }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total) => `${total} record${total === 1 ? '' : 's'}`,
            }}
            onChange={handleTableChange}
          />
        </div>

        <OfflineProductSalesFormDrawer
          open={drawerOpen}
          editingUuid={editingUuid}
          productTypes={productTypes}
          rmOptions={rmOptions}
          rmUnavailable={rmUnavailable}
          partnerUnavailable={partnerUnavailable}
          onClose={() => {
            setDrawerOpen(false);
            setEditingUuid(null);
          }}
          onSuccess={handleFormSuccess}
        />
      </div>
    </DashboardPageWrapper>
  );
}
